import React, { useState, useContext, useEffect } from 'react';
import { toast } from 'react-hot-toast';
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { FiSettings } from "react-icons/fi";
import { UserContext } from "../../context/UserContext";
import { BASELINE, AFTER_LOGIN } from '../../util';
import axios from "axios";
import PromotionModal from "../../Components/popmodal";
import SettingModal from "./setting";
import DarkSatModal from './sat_survey';

const sections = [
  {
    name: "Digital SAT 8 & 9",
    id: 1, path: 14, type: "PSAT89", select: "SAT89", url: "sat89",
    image: "https://images.unsplash.com/photo-1580582932707-520aed937b7b?w=640&q=80",
    description: "Adaptive PSAT 8/9 preparation with full-length practice tests and detailed score analytics.",
    badge: "PSAT 8/9", badgeColor: "bg-violet-500", level: "Gr 8–9",
  },
  {
    name: "Digital NMSQT",
    id: 2, path: 10, type: "NMSQT", select: "SAT10", url: "nmsqt",
    image: "https://images.unsplash.com/photo-1481627834876-b7833e8f5570?w=640&q=80",
    description: "National Merit scholarship qualifying test prep with targeted section-by-section practice.",
    badge: "NMSQT", badgeColor: "bg-indigo-500", level: "Gr 10–11",
  },
  {
    name: "Digital SAT",
    id: 3, path: 1, type: "SAT", select: "SAT", url: "sat",
    image: "https://images.unsplash.com/photo-1606326608606-aa0b62935f2b?w=640&q=80",
    description: "Complete SAT prep powered by AI — adaptive drills, full mock tests, and instant feedback.",
    badge: "SAT", badgeColor: "bg-blue-600", level: "Gr 10–12", ai: true,
  },
  {
    name: "Digital ACT",
    id: 4, path: 6, type: "ACT", select: "ACT", url: "act",
    image: "https://images.unsplash.com/photo-1456513080510-7bf3a84b82f8?w=640&q=80",
    description: "Full ACT coverage — English, Math, Reading, and Science with timed section practice.",
    badge: "ACT", badgeColor: "bg-orange-500", level: "Gr 10–12",
  },
  {
    name: "AP Calculus AB",
    id: 5, path: 18, type: "APCALCAB", select: "AP_Calc_AB", url: "apcalcab",
    image: "https://images.unsplash.com/photo-1635070041078-e363dbe005cb?w=640&q=80",
    description: "Limits, derivatives, and integrals — fully aligned to the AP Calculus AB exam format.",
    badge: "AP", badgeColor: "bg-emerald-500", level: "Advanced",
  },
  {
    name: "AP Calculus BC",
    id: 6, path: 22, type: "APCALCBC", select: "AP_Calc_BC", url: "apcalcbc",
    image: "https://images.unsplash.com/photo-1509228468518-180dd4864904?w=640&q=80",
    description: "Extends AB with infinite series, parametric equations, and polar coordinates.",
    badge: "AP", badgeColor: "bg-teal-500", level: "Advanced",
  },
];

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { when: "beforeChildren", staggerChildren: 0.1, duration: 0.5 }
  }
};

const itemVariants = {
  hidden: { y: 20, opacity: 0 },
  visible: {
    y: 0, opacity: 1,
    transition: { type: "spring", stiffness: 300, damping: 24 }
  }
};

const headerVariants = {
  hidden: { opacity: 0, y: -20 },
  visible: {
    opacity: 1, y: 0,
    transition: { duration: 0.6, ease: "easeOut" }
  }
};

const MainDashboard = ({
  setdashboard,
  setActiveIndex,
  setTestType,
  setSelectedTypes,
}) => {
  const { logoutUser, user, setid } = useContext(UserContext);
  const navigate = useNavigate();

  const [modalOpen, setModalOpen] = useState(false);
  const [isOpen2, setIsOpen2] = useState(false);
  const [testSchedules, setTestSchedules] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [newTestName, setNewTestName] = useState("Digital SAT");
  const [newTestDate, setNewTestDate] = useState(new Date());
  const [currentDate, setCurrentDate] = useState(new Date());
  const [darkSatModalOpen, setDarkSatModalOpen] = useState(false);
  const [pendingSection, setPendingSection] = useState(null);
  const [animationComplete, setAnimationComplete] = useState(false);

  useEffect(() => {
    const timer = setInterval(() => setCurrentDate(new Date()), 60 * 1000);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    if (!user) return;

    if (user.test) {
      setTestSchedules([{
        testName: user.test.testName || newTestName,
        date: user.test.date ? new Date(user.test.date) : newTestDate,
      }]);
    }

    const loadDashboard = async () => {
      const localDash = localStorage.getItem("dashboard");
      if (localDash === "1" || localDash === "3") {
        setdashboard(1);
        localStorage.removeItem("dashboard");
        return;
      }

      if (user.dashboard !== "0") {
        const dashId = parseInt(user.dashboard, 10);
        const foundSection = sections.find((s) => s.id === dashId);
        if (foundSection) {
          setdashboard(foundSection.path);
          setActiveIndex(foundSection.path);
          setTestType(foundSection.type);
          setSelectedTypes([foundSection.select]);
          try {
            const res = await axios.post(`${BASELINE}conversations`, {
              user_id: user.u_id,
              chat_type: foundSection.type,
            });
            setid(res.data.conversation_id);
            setdashboard(foundSection.path);
            setActiveIndex(foundSection.path);
            setTestType(foundSection.type);
            setSelectedTypes([foundSection.select]);
          } catch (err) {
            console.error("Error creating/finding conversation:", err);
          }
        }
      }
    };

    loadDashboard();
  }, [user]);

  function onLogout() {
    logoutUser();
    axios.post(BASELINE + "user/logout", {})
      .catch((error) => toast.error('Logout error: ' + error.message))
      .finally(() => window.location.replace(AFTER_LOGIN));
  }

  const handleSave = () => {};
  const openModal = () => setShowModal(true);
  const closeModal = () => setShowModal(false);

  const handleAddSchedule = async () => {
    const newSchedule = { testName: newTestName, date: newTestDate };
    try {
      const response = await axios.post(`${BASELINE}updateTest`, {
        user_id: user.u_id,
        test_data: newSchedule,
      });
      if (response.data.success) {
        setTestSchedules((prev) => [...prev, newSchedule]);
        closeModal();
      } else {
        throw new Error("Failed to add schedule");
      }
    } catch (error) {
      console.error("Failed to add the schedule:", error);
      alert("Failed to add the schedule. Please try again.");
    }
  };

  const getDdayText = () => {
    if (testSchedules.length === 0) return "FREE!";
    const today = new Date(currentDate);
    today.setHours(0, 0, 0, 0);
    const upcomingDates = testSchedules
      .map((sch) => new Date(sch.date.getTime()))
      .filter((d) => d >= today);
    if (upcomingDates.length === 0) return "No upcoming test date";
    const earliest = new Date(Math.min(...upcomingDates.map((d) => d.getTime())));
    earliest.setHours(0, 0, 0, 0);
    const diffDays = Math.ceil((earliest - today) / (1000 * 60 * 60 * 24));
    if (diffDays === 0) return "Today is Test Day!";
    if (diffDays < 0) return "All tests finished";
    return `D-${diffDays}`;
  };

  const proceedWithSection = async (section) => {
    await axios.post(`${BASELINE}setdashboard`, {
      u_id: user.u_id,
      dashboard: section.id,
    });
    // 서버와 동일하게 메모리상의 user.dashboard도 갱신
    if (user) user.dashboard = String(section.id);

    const isAllowedACT = section.type !== "ACT" || user?.mmt;
    const isAllowedAPCALC =
      (section.type !== "APCALCAB" && section.type !== "APCALCBC") ||
      user?.premium !== 0;

    if (isAllowedACT && isAllowedAPCALC) {
      try {
        const res = await axios.post(`${BASELINE}conversations`, {
          user_id: user.u_id,
          chat_type: section.type,
        });
        setid(res.data.conversation_id);
        setSelectedTypes([section.select]);
        setdashboard(section.path);
        setActiveIndex(section.path);
        setTestType(section.type);
        navigate(`/main/${section.url}`, { replace: false });
      } catch (err) {
        console.error("Error creating/finding conversation:", err);
      }
    } else {
      setModalOpen(true);
    }
  };

  const handleSurveyCompletion = async () => {
    localStorage.setItem("satsurveyCompleted", "true");
    user.satsurvey = true;
    setDarkSatModalOpen(false);
    if (pendingSection) {
      await proceedWithSection(pendingSection);
      setPendingSection(null);
    }
  };

  const justclosed = async () => setDarkSatModalOpen(false);

  const handleCardClick = async (section) => {
    const isAccessible = section.type === "SAT";

    if (!isAccessible) {
      setModalOpen(true);
      return;
    }

    if (section.type === "SAT") {
      if (!user?.satsurvey && localStorage.getItem("satsurveyCompleted") !== "true") {
        setPendingSection(section);
        setDarkSatModalOpen(true);
        return;
      }
    }

    await proceedWithSection(section);
  };

  return (
    <>
      <DarkSatModal
        user={user}
        isOpen={darkSatModalOpen}
        onClose={handleSurveyCompletion}
        justclose={justclosed}
      />

      <PromotionModal isOpen={modalOpen} onClose={() => setModalOpen(false)} />

      {!animationComplete && (
        <motion.div
          className="fixed inset-0 z-50 flex items-center justify-center bg-white"
          initial={{ opacity: 1 }}
          animate={{ opacity: 0 }}
          transition={{ duration: 0.8, delay: 1.5 }}
          onAnimationComplete={() => setAnimationComplete(true)}
        >
          <motion.div
            initial={{ scale: 0.8, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ duration: 0.5 }}
          >
            <motion.img
              src="/dashboard/icon.ico"
              alt="Logo"
              className="h-24 w-auto"
              initial={{ y: 0 }}
              animate={{ y: [-10, 10, -10] }}
              transition={{ duration: 1.5, repeat: 1, ease: "easeInOut" }}
            />
          </motion.div>
        </motion.div>
      )}

      <motion.div
        variants={containerVariants}
        initial="hidden"
        animate="visible"
        className="min-h-screen"
        style={{ background: "#eef2f7" }}
      >
        <div className="max-w-5xl mx-auto px-4 md:px-6">

          {/* ── Hero Banner ── */}
          <motion.div
            variants={headerVariants}
            className="relative mt-6 mb-8 rounded-2xl overflow-hidden"
            style={{ background: "linear-gradient(135deg, #1e3a8a 0%, #1d4ed8 55%, #3b82f6 100%)" }}
          >
            <div className="relative flex flex-col md:flex-row items-start md:items-center justify-between px-8 py-10 gap-6">
              <div>
                <motion.p
                  className="text-blue-200 text-sm font-medium tracking-widest uppercase mb-2"
                  initial={{ opacity: 0, x: -16 }} animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.5, delay: 0.2 }}
                >
                  Welcome back
                </motion.p>
                <motion.h1
                  className="text-3xl md:text-4xl font-bold text-white mb-2"
                  initial={{ opacity: 0, x: -16 }} animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.5, delay: 0.35 }}
                >
                  {user?.first_name} {user?.last_name}
                </motion.h1>
                <motion.p
                  className="text-blue-100 text-sm md:text-base mb-5"
                  initial={{ opacity: 0 }} animate={{ opacity: 1 }}
                  transition={{ duration: 0.5, delay: 0.5 }}
                >
                  Elevate your skills — Learn, Practice, and Conquer!
                </motion.p>
                <motion.div
                  className="flex flex-wrap gap-2"
                  initial={{ opacity: 0 }} animate={{ opacity: 1 }}
                  transition={{ duration: 0.5, delay: 0.6 }}
                >
                  {[
                    { value: "6", label: "Courses" },
                    { value: "✦", label: "AI Powered" },
                    { value: "✓", label: "Full Tests" },
                  ].map(stat => (
                    <div key={stat.label} className="flex items-center gap-1.5 bg-white/10 border border-white/20 rounded-lg px-3 py-1.5">
                      <span className="text-white font-bold text-xs">{stat.value}</span>
                      <span className="text-blue-200 text-xs">{stat.label}</span>
                    </div>
                  ))}
                </motion.div>
              </div>

              {/* D-day card */}
              <motion.div
                className="flex-shrink-0 flex flex-col items-center justify-center bg-white/15 border border-white/30 backdrop-blur-sm rounded-2xl px-8 py-5 min-w-[180px] cursor-pointer hover:bg-white/25 transition"
                onClick={openModal}
                initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.5, delay: 0.4 }}
                whileHover={{ scale: 1.04 }}
              >
                <p className="text-blue-200 text-xs font-semibold uppercase tracking-widest mb-1">
                  {testSchedules.length > 0 ? testSchedules[0].testName : "No Test Set"}
                </p>
                <p className="text-white text-4xl font-black leading-none">{getDdayText()}</p>
                <p className="text-blue-100 text-xs mt-2">
                  {testSchedules.length > 0
                    ? new Date(testSchedules[0].date).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })
                    : "Tap to set your test date"}
                </p>
              </motion.div>
            </div>
          </motion.div>

          {/* ── Section Header ── */}
          <motion.div className="mb-5 flex items-center justify-between" variants={headerVariants}>
            <div>
              <h2 className="text-slate-900 text-xl font-bold">Available Courses</h2>
              <p className="text-slate-500 text-sm mt-0.5">Choose a subject to start practicing</p>
            </div>
            <span className="text-xs font-semibold text-slate-500 bg-slate-200 px-3 py-1 rounded-full">
              6 Courses
            </span>
          </motion.div>

          {/* ── Course Cards ── */}
          <motion.div
            className="pb-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5"
            variants={containerVariants}
          >
            {sections.map((section, index) => {
              const isLocked = section.type !== "SAT";
              return (
                <motion.div
                  key={index}
                  className={`group overflow-hidden bg-white border border-slate-200 cursor-pointer transition-all duration-300 ${!isLocked ? 'hover:shadow-xl hover:-translate-y-1' : ''}`}
                  onClick={() => handleCardClick(section)}
                  variants={itemVariants}
                  style={{ boxShadow: "0 1px 8px rgba(30,58,138,0.06)" }}
                >
                  {/* Image */}
                  <div className="relative h-44 overflow-hidden">
                    <img
                      src={section.image}
                      alt={section.name}
                      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/55 via-black/10 to-transparent" />

                    {/* Subject badge */}
                    <span className={`absolute top-3 left-3 ${section.badgeColor} text-white text-[11px] font-bold px-2.5 py-1 rounded-full tracking-wide shadow-sm`}>
                      {section.badge}
                    </span>

                    {/* AI badge */}
                    {section.ai && (
                      <span className="absolute top-3 right-3 text-[11px] font-bold px-2.5 py-1 rounded-full text-white shadow-sm"
                        style={{ background: "linear-gradient(90deg,#3b82f6,#8b5cf6)" }}>
                        ✨ AI
                      </span>
                    )}

                    {/* Locked dim */}
                    {isLocked && <div className="absolute inset-0 bg-slate-50/30 backdrop-blur-[1px]" />}

                    {/* Course name on image */}
                    <div className="absolute bottom-0 left-0 p-4">
                      <h3 className="text-white text-base font-bold leading-snug drop-shadow">{section.name}</h3>
                    </div>
                  </div>

                  {/* Card body */}
                  <div className="p-4">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-[11px] text-slate-400 bg-slate-100 px-2 py-0.5 rounded-full font-medium">
                        {section.level}
                      </span>
                      <div className="flex items-center gap-1">
                        <div className={`w-1.5 h-1.5 rounded-full ${isLocked ? 'bg-slate-300' : 'bg-emerald-400'}`} />
                        <span className="text-[11px] text-slate-400">{isLocked ? 'Coming Soon' : 'Available'}</span>
                      </div>
                    </div>

                    <p className="text-slate-500 text-xs leading-relaxed mb-4">{section.description}</p>

                    <div className="pt-3 border-t border-slate-100">
                      {isLocked ? (
                        <span className="text-xs font-semibold text-slate-400 flex items-center gap-1.5">
                          <span className="text-base">🔒</span> Coming Soon
                        </span>
                      ) : (
                        <span className="text-xs font-bold text-blue-600 flex items-center gap-1 group-hover:gap-2 transition-all duration-200">
                          Start Now <span className="group-hover:translate-x-0.5 transition-transform inline-block">→</span>
                        </span>
                      )}
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </motion.div>

        </div>

        {/* Test Schedule Modal */}
        {showModal && (
          <div
            className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 backdrop-blur-sm"
            onClick={closeModal}
          >
            <motion.div
              className="relative w-full max-w-md mx-4 rounded-2xl shadow-2xl p-6 bg-white border border-slate-200"
              onClick={(e) => e.stopPropagation()}
              initial={{ scale: 0.9, opacity: 0 }} animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              transition={{ type: "spring", duration: 0.4 }}
            >
              <button className="absolute top-4 right-4 text-slate-400 hover:text-slate-700 text-2xl leading-none" onClick={closeModal}>
                &times;
              </button>
              <h2 className="text-slate-800 text-xl font-bold mb-5">Register Your Test Date</h2>

              <label className="block mb-1 text-sm font-medium text-slate-500">Select Test</label>
              <select
                value={newTestName}
                onChange={(e) => setNewTestName(e.target.value)}
                className="mb-4 w-full rounded-lg px-3 py-2 text-sm text-slate-800 bg-slate-50 border border-slate-200 focus:outline-none focus:border-blue-500"
              >
                {["Digital SAT 8 & 9", "Digital NMSQT", "Digital SAT", "Digital ACT", "AP Calculus AB", "AP Calculus BC"].map((opt, i) => (
                  <option key={i} value={opt}>{opt}</option>
                ))}
              </select>

              <label className="block mb-1 text-sm font-medium text-slate-500">Pick Date</label>
              <DatePicker
                selected={newTestDate}
                onChange={(date) => date && setNewTestDate(date)}
                className="mb-5 w-full rounded-lg px-3 py-2 text-sm text-slate-800 bg-slate-50 border border-slate-200 focus:outline-none"
              />

              <button
                className="w-full py-2.5 rounded-xl bg-blue-600 hover:bg-blue-500 text-white font-semibold text-sm transition"
                onClick={handleAddSchedule}
              >
                Add Schedule
              </button>
            </motion.div>
          </div>
        )}

        <SettingModal
          isOpen={isOpen2}
          onClose={() => setIsOpen2(false)}
          user={user}
          onSave={handleSave}
          onLogout={onLogout}
        />
      </motion.div>
    </>
  );
};

export default MainDashboard;
